import React, { Key, useMemo, useState } from "react";
import { App as AntdApp, Button, Modal, Tag, Tooltip } from "antd";
import { useMutation, useQuery } from "@apollo/client";
import { AdvancedImage } from "@cloudinary/react";
import { thumbnail } from "@cloudinary/url-gen/actions/resize";
import { ColumnsType } from "antd/es/table";
import Frame from 'react-frame-component';
import { ArrowTopRightOnSquareIcon, EyeIcon, InformationCircleIcon, PencilIcon, TrashIcon } from "@heroicons/react/24/outline";
import Table from "../../lists/Table/Table";
import { useOutletContext } from "react-router-dom";
import PublicationStatus from "../../badges/PublicationStatus";
import { cld, getTrafficDriverImageUrl } from "../../../utils/cloudinary";
import ActionButton from "../../lists/Table/shared/ActionButton";
import {TagsCell} from "../../lists/Table/shared/TagsCell";
import {DELETE_TRAFFIC_DRIVER, UPDATE_TRAFFIC_DRIVER, GET_CONTENT_CATEGORIES, UPDATE_TRAFFIC_DRIVERS, GET_TRAFFIC_DRIVERS, CREATE_TRAFFIC_DRIVER} from '../../../utils/api/trafficDriver';
import {CampaignType, Teaser} from "../../../__generated__/graphql";
import { useCurrentUser } from "../../../utils/CurrentUserContext";
import { withAsyncErrorToast } from "../../../utils/withToast";
import { videoStatusDisplay } from "../../../utils/video-utils";

import MultipleTrafficDriversEditModal from "../../modals/MultipleTrafficDriversEditModal";
import { useQueryVariables } from "../../../utils/hooks";
import TrafficDriverModal from "../../modals/TrafficDriverModal";

interface TrafficDriversFormProps {
  websites: { _id: string; name: string }[];
}

// Preview URLs are now generated directly by the backend previewLinks field

const getColumns = (
  onEdit: any,
  onDelete: any,
  onPreview: any,
  selectedRows: Key[],
  readonly: boolean,
  campaign,
  allowTeaserRibbon: boolean,
  allowTeaserVideos: boolean,
): ColumnsType<Teaser> => [
  {
    key: "headline",
    title: "Headline",
    dataIndex: "headline",
  },
  ...(allowTeaserRibbon ? [{
    key: "ribbon",
    title: <div className="flex">
      Ribbon
      <Tooltip title="Some placements might not support ribbon">
        <InformationCircleIcon
          className='flex ml-1 mr-3 h-5 w-5 text-gray-400 hover:text-gray-00 hover:cursor-pointer'
          data-tooltip-target='tooltip-default'
        />
      </Tooltip>
    </div>,
    dataIndex: "ribbon",
  }] : []),
  {
    key: "imageId",
    title: "Banner",
    dataIndex: "imageId",
    render: (imageId) => {
      if (!imageId) {
        return null;
      }
      const image = cld
        .image(imageId)
        .resize(thumbnail().height(140))
        .format("png");
      return <AdvancedImage cldImg={image} />;
    },
  },
  ...(allowTeaserVideos ? [{
    key: "video",
    title: "Video",
    dataIndex: "video",
    render: (video: any) => {
      const display = videoStatusDisplay(video?.status);
      if (!display) return null;
      if (display.tone === "success" && video.thumbnailUrl) {
        return <img src={video.thumbnailUrl} alt="Video thumbnail" style={{ height: 140 }} loading="lazy" />;
      }
      return <Tag color={display.tone}>{display.label}</Tag>;
    },
  }] : []),
  {
    key: "websites",
    title: "Websites",
    dataIndex: "previewLinks",
    render: (value, row) => TagsCell(
      (value || [])
      .map((previewLink) => {
        const {websiteId, websiteName, url} = previewLink

        return {_id: websiteId, name: (
          <a className='flex items-center' rel='noreferrer' target='_blank' href={url}>
            {websiteName}
            <ArrowTopRightOnSquareIcon
              className="h-4 w-4 ml-3"
              aria-hidden="true"
            />
          </a>
        )}
      })
    ),
  },
  {
    key: "status",
    title: "Status",
    dataIndex: "status",
    render: (status) => <PublicationStatus status={status} />,
  },
  {
    key: "actions",
    title: (
      <div className="px-3">
        {selectedRows.length > 0 && <ActionButton
          className="mx-3"
          tooltip={`Edit ${selectedRows.length} teasers`}
          row={selectedRows}
          onClick={onEdit}
          Icon={PencilIcon}
          padding={false}
        />
        }
        {selectedRows.length > 0 && <ActionButton
            className="mx-3"
            tooltip={`Delete ${selectedRows.length} teasers`}
            row={selectedRows}
            onClick={onDelete}
            Icon={TrashIcon}
            danger
            padding={false}
          />
        }
      </div>
    ),
    render: (_, row) => (
      <div className="px-3">
        {!readonly && <ActionButton
          tooltip="Edit"
          row={[row._id]}
          onClick={onEdit}
          Icon={PencilIcon}
        />}
        {!readonly && <ActionButton
          tooltip="Delete"
          row={[row._id]}
          onClick={onDelete}
          Icon={TrashIcon}
          danger
        />}
        <ActionButton
          tooltip="Preview"
          row={row}
          onClick={onPreview}
          Icon={EyeIcon}
        />
      </div>
    ),
  },
];

const TrafficDriversForm: React.FC<TrafficDriversFormProps> = ({
  websites,
}) => {
  const { notification } = AntdApp.useApp();
  const { user } = useCurrentUser();
  const allowedTeaserFormats = user?.company?.allowedTeaserFormats || [];
  const allowTeaserRibbon = user?.company?.allowTeaserRibbon
  const allowTeaserVideos = user?.company?.allowTeaserVideos


  const [modalVisible, setModalVisible] = useState<boolean>(false);
  const [multipleEdit, setMultipleEdit] = useState(false);
  const [multipleTrafficDrivers, setMultipleTrafficDrivers] = useState<any[]>([]);
  const [previewVisible, setPreviewVisible] = useState(false);
  // Kept separate from `trafficDriver` on purpose: that one doubles as "we are editing this
  // teaser", so previewing into it opens the next Add Teaser in edit mode, pre-filled with the
  // previewed row and carrying its __typename into TeaserUpdateByID.
  const [previewTeaser, setPreviewTeaser] = useState<Teaser>();
  const [screen, setScreen] = useState<string>('desktop');
  const { campaign, publication } = useOutletContext<any>();

  const isCampaignOwner = campaign.ownerCompanyId === user.companyId

  const [selectedRows, setSelectedRows] = useState<Key[]>([]);

  const [trafficDriver, setTrafficDriver] = useState<Teaser>();

  const {variables, setPagination, setFilters} = useQueryVariables()

  const {data, loading} = useQuery(GET_TRAFFIC_DRIVERS, {
    fetchPolicy: 'cache-and-network',
    nextFetchPolicy: 'cache-first',
    variables: {
      ...variables,
      filters: {
        ...variables.filters,
        campaignId: campaign._id,
        publicationId: publication?._id
      }
    }
  })

  const trafficDrivers = (data?.trafficDrivers.items || []) as Teaser[]

  const [createTeaser, { loading: saving }] = useMutation(
    CREATE_TRAFFIC_DRIVER, {
      refetchQueries: ["TeaserMany"],
      awaitRefetchQueries: true,
    }
  );
  const [updateTrafficDriver] = useMutation(UPDATE_TRAFFIC_DRIVER, {
    refetchQueries: ["TeaserMany"],
    awaitRefetchQueries: true,
  });
  const [updateTrafficDrivers] = useMutation(UPDATE_TRAFFIC_DRIVERS, {
    refetchQueries: ["TeaserMany"],
    awaitRefetchQueries: true,
  })
  const [deleteTrafficDriver] = useMutation(DELETE_TRAFFIC_DRIVER, {
    refetchQueries: ["TeaserMany"],
    awaitRefetchQueries: true,
  });

  const handleCreateTrafficDrivers = async (form: any) => {
    if (trafficDriver) {
      const { _id, websites, website, publications, video, targeting, previewLinks, ...record } = form;
      //const targeting = record.targeting && omit(record.targeting, '__typename');

      return withAsyncErrorToast('Update teaser', async () => {
        await updateTrafficDriver({
          variables: { id: trafficDriver._id, record: { ...record, /*targeting*/ } },
        })
      },
        undefined,
        notification
      );
    } else {
      const record = {
        campaignId: campaign._id,
        websiteIds: form.websiteIds,
        headline: form.headline,
        ribbon: form.ribbon,
        teaser: form.teaser,
        imageId: form.imageId,
        videoId: form.videoId,
        format: form.format,
        destinationUrl: form.destinationUrl,
      }

      return withAsyncErrorToast('Create teaser', async () => {
        await createTeaser({
          variables: { record },
        })
      },
        undefined,
        notification
      );
    }
  };

  const handleEdit = (rows: string[]) => {
    if (rows.length > 1) {
      setMultipleTrafficDrivers(trafficDrivers.filter(({_id}) => rows.includes(_id)))
      setMultipleEdit(true)
    } else {
      const trafficDriver = trafficDrivers.find(({ _id }) => _id === rows[0]) || {} as any
      const { __typename, status, publication, ...newTrafficDriver } =
        trafficDriver;
      setTrafficDriver(newTrafficDriver);
      setModalVisible(true);
    }
  };

  const handleMultipleEdit = ({items}: {items: any[]}) => {
    const records: any[] = items.map(({_id, headline, imageId, websiteIds}) => ({
      id: _id,
      record: {headline, imageId, websiteIds}
    }))

    return withAsyncErrorToast('Update multiple teasers', async () => {
      await updateTrafficDrivers({
        variables: {records}
      })
    },
      undefined,
      notification
    )
  }
  const handleDelete = (rows: string[]) => {
    Modal.confirm({
      title: "Are you sure?",
      content: `Are you sure you want to delete ${rows.length} teaser(s)?`,
      okText: "Delete",
      okType: 'danger',
      onOk: () =>
        withAsyncErrorToast('Delete teasers', async () => {
          await Promise.all(rows.map((id) => deleteTrafficDriver({ variables: { id } })))
        }
        , undefined, notification),
    });
  };

  const handleOnPreview = (row: any) => {
    setPreviewTeaser(row)
    setPreviewVisible(true)
  }

  const columns = getColumns(handleEdit, handleDelete, handleOnPreview, selectedRows, !isCampaignOwner, campaign, allowTeaserRibbon, allowTeaserVideos);

  // Set default values including format if only one option exists
  const getDefaultValues = () => {
    if (trafficDriver) {
      return trafficDriver;
    }

    const defaults: any = {};

    // Auto-set websites if only one
    if (websites.length === 1) {
      defaults.websiteIds = [websites[0]._id];
    }

    // Auto-set format if only one option
    if (allowedTeaserFormats.length === 1) {
      defaults.format = allowedTeaserFormats[0].type;
    }

    return Object.keys(defaults).length > 0 ? defaults : undefined;
  };

  const defaultValues = getDefaultValues();
  const isLocalhost = useMemo(
    () => typeof window !== 'undefined' && window.location.hostname === 'localhost',
    []
  )

  return (
    <div className="flex-1 relative bg-white rounded-md px-4 py-4 pb-20 mb-10 space-y-6 sm:space-y-5">
      {isLocalhost && (
        <div className="flex justify-end">
          <Button
            danger
            size="small"
            onClick={() => {
              withAsyncErrorToast('Traffic driver save', async () => {
                throw new Error('Local test failure: simulated save error')
              }
              , undefined, notification).catch(() => {})
            }}
          >
            Simulate teaser save error
          </Button>
        </div>
      )}
      <Table
        columns={columns}
        data={trafficDrivers || []}
        actionButtonText={isCampaignOwner ? "Add Teaser" : undefined}
        onActionButtonClick={() => setModalVisible(true)}
        selectable={isCampaignOwner}
        defaultRowSelection={selectedRows}
        rowKey="_id"
        onRowSelectionChange={setSelectedRows}
        loading={loading}
        pagination={{
          total: data ? data.trafficDrivers.meta.totalItems : 0,
          pageSize: variables.perPage,
          current: variables.page
        }}
        onChange={(pagination, filters) => {
          setPagination(pagination)
          setFilters(filters)
        }}
      />

      <TrafficDriverModal
        open={modalVisible}
        editMode={!!trafficDriver}
        defaultValues={defaultValues}
        onCancel={() => {
          setModalVisible(false);
          setTrafficDriver(undefined);
        }}
        onOk={handleCreateTrafficDrivers}
        websites={websites}
        allowTeaserRibbon={allowTeaserRibbon}
        allowedTeaserFormats={allowedTeaserFormats}
        allowTeaserVideos={allowTeaserVideos}
        campaignType={campaign?.type}
      />
      <Modal
        title="Preview traffic driver"
        width={800}
        open={previewVisible}
        onCancel={() => setPreviewVisible(false)}
        onOk={() => setPreviewVisible(false)}
        okText="Ok"
      >
          {/* @ts-ignore */}
          {previewTeaser && <contimo-widget dummy teasers={`[${JSON.stringify(previewTeaser)}]`}></contimo-widget>}
      </Modal>
      <MultipleTrafficDriversEditModal
        title="Edit multiple teasers"
        open={multipleEdit}
        onCancel={() => setMultipleEdit(false)}
        okText="Save"
        defaultValues={{items: multipleTrafficDrivers}}
        onOk={handleMultipleEdit}
      />
    </div>
  );
};

export default TrafficDriversForm;
